import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { RootState } from "../index";
import {
  fetchBannedIp,
  fetchBannedIps,
  createBannedIp,
  updateBannedIp,
  destroyBannedIp
} from "../../actions/mine_craft/bannedIpsActions";

export interface BannedIpInterface {
  url: string;
  id: number;
  ip: string;
  reason: string;
  expires_at: string | null;
  permanent: boolean;
  active: boolean;
  user_id: number | null;
  created_at: string;
  updated_at: string;
}

export interface initialInterface {
  error: string | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  count: number;
  editId: EntityId | null;
  editError: string | null;
  newParams: BannedIpInterface | undefined;
  newError: string | null;
}

const initialState: initialInterface = {
  error: null,
  status: 'idle',
  count: 0,
  editId: null,
  editError: null,
  newParams: undefined,
  newError: null
};

const adaptor = createEntityAdapter<BannedIpInterface>();

const slice = createSlice({
  name: 'bannedIps',
  initialState: adaptor.getInitialState(initialState),
  reducers: {
    setNewParams: (state, action) => {
      state.newParams = action.payload;
      state.newError  = initialState.newError;
    },
    setNewError: (state, action) => {
      state.newError = action.payload;
    },
    setEditId: (state, action) => {
      state.editId    = action.payload;
      state.editError = initialState.editError;
    },
    setEditError: (state, action) => {
      state.editError = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      // #index
      .addCase(fetchBannedIps.pending, (state, _action) => {
        state.status = 'loading'
      })
      .addCase(fetchBannedIps.fulfilled, (state, action) => {
        state.count = action.payload.count;
        state.entities = action.payload.bannedIps;
        state.ids = action.payload.ids;
        state.status = 'succeeded';
      })
      .addCase(fetchBannedIps.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Unknown Error';
      })
      // #show
      .addCase(fetchBannedIp.fulfilled, (state, action) => {
        const bannedIp = Object.values(action.payload.bannedIp)[0];
        state.entities[bannedIp.id] = bannedIp;
      })
      // #create
      .addCase(createBannedIp.fulfilled, (state, action) => {
        const bannedIp = Object.values(action.payload.bannedIp)[0];
        state.entities[bannedIp.id] = bannedIp;
      })
      .addCase(createBannedIp.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.newError = error || action.error.message || 'Unknown Error';
      })
      // #update
      .addCase(updateBannedIp.fulfilled, (state, action) => {
        const bannedIp = Object.values(action.payload.bannedIp)[0];
        state.entities[bannedIp.id] = bannedIp;
      })
      .addCase(updateBannedIp.rejected, (state, action) => {
        const error = Array.isArray(action.payload) ? action.payload[0] : null;
        state.editError = error || action.error.message || 'Unknown Error';
      })
      // #destroy
      .addCase(destroyBannedIp.fulfilled, (state, action) => {
        adaptor.removeOne(state, action.payload.id);
        state.count = Math.max(0, state.count - 1);
      })
  }
});

const reducer = slice.reducer;
export default reducer;

export const { setEditId, setEditError, setNewParams, setNewError } = slice.actions;

export const {
  selectById: selectBannedIpById,
  selectIds: selectBannedIpIds,
  selectEntities: selectBannedIpEntities,
}  = adaptor.getSelectors<RootState>(state => state.mineCraft.bannedIps);
